import { useCallback, useEffect, useState } from 'react'
import { getWebinarSuggestions } from '@domain/features/webinar-suggestions/webinar-suggestions-feature'
import type { WebinarSuggestion } from '@domain/models/webinar-suggestions'

interface UseWebinarSuggestionsOptions {
  enabled?: boolean
  limit?: number
}

function dedupeSuggestionsByUid(items: WebinarSuggestion[]): WebinarSuggestion[] {
  const byUid = new Map<string, WebinarSuggestion>()
  for (const item of items) {
    if (!byUid.has(item.uid)) {
      byUid.set(item.uid, item)
    }
  }
  return [...byUid.values()]
}

export function useWebinarSuggestions(options?: UseWebinarSuggestionsOptions) {
  const [suggestions, setSuggestions] = useState<WebinarSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)

  const reload = useCallback(() => {
    setReloadTick((current) => current + 1)
  }, [])

  useEffect(() => {
    if (options?.enabled === false) {
      setSuggestions([])
      setError(null)
      setLoading(false)
      return
    }

    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        setError(null)
        const result = await getWebinarSuggestions(options?.limit)
        if (!cancelled) {
          setSuggestions(dedupeSuggestionsByUid(result))
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load webinar suggestions.',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [options?.enabled, options?.limit, reloadTick])

  return { suggestions, loading, error, reload }
}
